import { Component, Vue } from "vue-property-decorator";
import OrderService from "../../services/order_service";
import NetSuiteService from "../../services/netsuite_service";
import { OrderStatus } from "../../models/order/helpers";
import { AuthError, GenericError } from "../../services/error_service";
import { NotificationService } from "../../services/notification_service";
import { namespace } from "vuex-class";
import { StoreModule } from "../../store/types";
import { OrderGetters } from "../../store/modules/order/types";
import { UserGetters } from "../../store/modules/user/types";
import { LineItem } from "../../models/order/order";
import { ProductType } from "../../models/products/product";
import { Order } from "../../models/order/order";
import {
  CreateNetSuiteQuoteLineItem,
  CreateNetSuiteQuotePayload,
  CreateNetSuiteQuoteShippingAddress,
  FoundNetSuiteItemBySku,
  NetSuiteQuote
} from "../../models/netsuite/netsuite";
import { User } from "../../models/user/user";
import { NetSuiteQuoteStatus } from "../../models/netsuite/helpers";

@Component
export class OrderMixin extends Vue {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrderItems))
  items!: LineItem[];
  @(namespace(StoreModule.User).Getter(UserGetters.GetUser))
  user!: User;

  protected productType = ProductType;
  protected action: null | "edit" | "dupe" = null;
  protected showModal = false;
  protected id = "";
  protected status: OrderStatus | null = null;
  protected orderStatus = OrderStatus;
  protected orderService = new OrderService();
  protected netSuiteService = new NetSuiteService();
  protected netSuiteQuotes: Order[] = [];
  protected orders: Order[] = [];
  protected search = "";
  protected activeTab: string = NetSuiteQuoteStatus.Open;

  protected get sortedOrders(): Order[] {
    return this.orders
      .slice()
      .sort((a, b) => {
        //@ts-ignore
        return (new Date(b.created_at) as any) - (new Date(a.created_at) as any);
      })
      .filter(o => o.status !== OrderStatus.Converted);
  }

  protected get tabCounts(): Record<string, number> {
    const orders = this.sortedOrders;
    return {
      [NetSuiteQuoteStatus.Open]: orders.filter(
        o =>
          o.netsuite_id &&
          ((o as unknown) as NetSuiteQuote).status === NetSuiteQuoteStatus.Open
      ).length,
      [NetSuiteQuoteStatus.Processed]: orders.filter(
        o =>
          o.netsuite_id &&
          ((o as unknown) as NetSuiteQuote).status === NetSuiteQuoteStatus.Processed
      ).length,
      [NetSuiteQuoteStatus.Expired]: orders.filter(
        o =>
          o.netsuite_id &&
          ((o as unknown) as NetSuiteQuote).status === NetSuiteQuoteStatus.Expired
      ).length,
      Legacy: orders.filter(o => !o.netsuite_id).length
    };
  }

  protected get filteredOrders(): Order[] {
    let orders: Order[];

    if (this.activeTab === "Legacy") {
      orders = this.sortedOrders.filter(o => !o.netsuite_id);
    } else {
      orders = this.sortedOrders.filter(
        o => o.netsuite_id && o.status === this.activeTab
      );
    }

    if (this.search !== "") {
      const term = this.search.toLowerCase();
      const byCustomer = (orders as any[]).filter(item => {
        if (item.customer_id && item.customer_id.customer_name) {
          return item.customer_id.customer_name.toLowerCase().indexOf(term) !== -1;
        }
      });
      const byQuoteName = (orders as any[]).filter(item => {
        if (item.contact_details && item.contact_details.quote_name) {
          return item.contact_details.quote_name.toLowerCase().indexOf(term) !== -1;
        }
      });
      return byCustomer.concat(byQuoteName);
    }

    return orders;
  }

  // ToDo: FNSI-101 - Needs rewrite. Temporary logic since NetSuiteQuoteLineItems have not yet been converted to Order LineItem objects.
  protected getItemsTally(items: LineItem[]): number {
    let num = 0;
    for (const item of items) {
      const netSuiteItemQty = (item as any).quantity;
      if (netSuiteItemQty && !isNaN(netSuiteItemQty)) {
        num += Math.abs(netSuiteItemQty);
      } else if (item.qty && !isNaN(item.qty)) {
        num += item.qty;
      }
    }
    return num;
  }

  protected getTotal(items: LineItem[], costDetails: any): number {
    if (costDetails && costDetails.line_item_total) {
      return costDetails.line_item_total;
    }
    let num = 0;
    for (const item of items) {
      num = num + item.qty * item.unit_price * item.multiplier;
    }
    return num;
  }

  protected stringifyItemNotes(item: LineItem): string {
    let notesStr = "";
    if (item.notes) {
      notesStr += `Item Notes: ${item.notes}`;
    }
    if (item.item_details && item.item_details.notes) {
      for (const note of item.item_details.notes) {
        if (!note.content) continue;
        if (notesStr !== "") notesStr += "\n";
        notesStr += `${note.field.toUpperCase()} Notes: ${note.content}`;
      }
    }
    return notesStr;
  }

  protected isNetSuiteQuote(order: Order): boolean {
    return !!order.netsuite_id;
  }

  protected confirm(): void {
    const order = this.orders.find(o => o._id === this.id);
    if (order && this.isNetSuiteQuote(order)) {
      console.log(
        "Edit/duplicate not yet supported for NetSuite quotes.",
        order
      );
      return;
    }
    if (this.action === "edit") {
      this.editOpenQuote();
    } else {
      this.duplicateOrder();
    }
  }

  protected duplicateOrder(): void {
    this.$router.push("/cart/" + this.id + "?status=" + this.status);
  }

  protected editOpenQuote(): void {
    this.$router.push("/cart/" + this.id + "?edit=true&status=" + this.status);
  }

  protected async download(id: string): Promise<void> {
    try {
      NotificationService.show(
        `Downloading legacy quote #${id.slice(-7)}...`,
        "warning"
      );
      const blobData = await this.orderService.downloadOrderInvoice(id);
      this.forceDownload(
        blobData,
        `Frankford Umbrellas Submitted Quote #${id.slice(-7)}`
      );
      NotificationService.show(
        `Legacy quote #${id.slice(-7)} downloaded.`,
        "success"
      );
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected async downloadQuote(id: string): Promise<void> {
    try {
      NotificationService.show(
        `Downloading legacy quote #${id.slice(-7)}...`,
        "warning"
      );
      const blobData = await this.orderService.downloadQuoteInvoice(id);
      this.forceDownload(
        blobData,
        `Frankford Umbrellas Open Quote #${id.slice(-7)}`
      );
      NotificationService.show(
        `Legacy quote #${id.slice(-7)} downloaded.`,
        "success"
      );
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected async emailNetSuiteQuote(quoteId: number): Promise<void> {
    try {
      NotificationService.show(`Emailing quote #${quoteId}...`, "warning");
      await this.netSuiteService.sendQuoteEmail(quoteId);
      NotificationService.show(`Quote #${quoteId} email sent.`, "success");
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected async downloadNetSuiteQuote(
    quoteId: number,
    quoteNumber: number
  ): Promise<void> {
    try {
      NotificationService.show(
        `Downloading quote #${quoteNumber}...`,
        "warning"
      );
      const blobData = await this.netSuiteService.getQuotePdf(quoteId);
      this.forceDownload(blobData, `Frankford Umbrellas Quote #${quoteNumber}`);
      NotificationService.show(`Quote #${quoteNumber} downloaded.`, "success");
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected forceDownload(blobData: any, fileName: string): void {
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
  }

  protected close(): void {
    this.id = "";
    this.action = null;
    this.status = null;
    this.showModal = false;
  }

  protected toggleConfirmationModal(
    id: string,
    action: "edit" | "dupe",
    status: OrderStatus
  ): void {
    this.action = action;
    this.status = status;
    this.id = id;
    this.showModal = true;
  }

  protected editItem(idx?: number): void {
    const index = idx !== undefined ? idx : this.items.length - 1;
    const item = this.items[index];
    const indexAsString = index > 0 ? index.toString() : "0";
    const handle =
      item.product_type === this.productType.Base
        ? item.model
        : item.product_line;
    this.$router.push({
      name: "ItemCustomization",
      query: { handle: handle, cart: indexAsString },
      params: {
        product: item.product_type,
        edit: "true"
      }
    });
  }

  // Triggered from child component, <Search />
  protected submitSearch(searchTerm: string): void {
    this.search = searchTerm;
  }

  /**
   * @param specifiedNetSuiteQuotes either the user's NetSuite quotes or the customer's NetSuite quotes
   * @description Converts the NetSuite quotes to the Order format
   * @output sets the NetSuite quotes for this mixin
   */
  protected convertNetSuiteQuotes(
    specifiedNetSuiteQuotes: NetSuiteQuote[]
  ): void {
    for (const quote of specifiedNetSuiteQuotes) {
      const convertedOrder: Order = ({
        _id: quote["document number"],
        netsuite_id: quote["quote id"],
        created_at: quote["date created"],
        customer_id: {
          customer_name: quote["customer name"],
          netsuite_id: quote["customer id"]
        },
        contact_details: {
          company_name: quote["customer name"],
          quote_name: quote["quote title"]
        },
        // items: [],
        items: quote.line_items ? quote.line_items : ([] as LineItem[]), // ToDo: FNSI-101 - convert NetSuiteQuoteLineItems to Order LineItems
        customer_netsuite_price_level_discount:
          quote.price_level_discount ?? undefined,
        status: quote["status"],
        cost_details: {
          line_item_total: quote["total amt"]
        }
      } as unknown) as Order;
      // ToDo: FNSI-101 - write new function to convert the NetSuite quote line_items to the Order items format
      this.netSuiteQuotes.push(convertedOrder);
    }
  }

  // ToDo: FNSI-101 - further refactor this function as needed, if needed
  protected convertNetSuiteQuoteLineItemsToOrderLineItems(
    netSuiteQuoteLineItems: NetSuiteQuote[]
  ): LineItem[] {
    return netSuiteQuoteLineItems.map(item => ({
      // _id: item["line item id"],
      product_type: item["product type"],
      model: item["model"],
      product_line: item["product line"],
      qty: item["quantity"],
      unit_price: item["unit price"],
      multiplier: item["multiplier"] || 1,
      description: item["description"],
      display_name: item["display_name"] || item["model"] || "",
      notes: item["notes"] || ""
    }));
  }

  protected async getSingleNetSuiteQuoteLineItems(
    netSuiteQuote: NetSuiteQuote
  ): Promise<void> {
    try {
      const fullQuote = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
        netSuiteQuote["quote id"]
      );
      console.log("fullQuote", fullQuote);
    } catch (err) {
      console.error("Error getting NetSuite quote line items", err);
    }
  }

  protected async getNetSuiteQuoteLineItems(
    netSuiteQuotes: NetSuiteQuote[]
  ): Promise<NetSuiteQuote[]> {
    if (!netSuiteQuotes.length) return netSuiteQuotes;

    await Promise.all(
      netSuiteQuotes.map(async quote => {
        try {
          quote.line_items = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
            quote["quote id"]
          );
        } catch (err) {
          console.error(
            `Failed to fetch line items for quote ${quote["quote id"]}`,
            err
          );
          quote.line_items = [];
        }
      })
    );

    return netSuiteQuotes;
  }

  protected async createNetSuiteQuote(order: Order): Promise<any> {
    try {
      const payload: CreateNetSuiteQuotePayload = await this.prepOrderToCreateNetSuiteQuote(
        order
      );
      const res = await this.netSuiteService.createNetSuiteQuote(payload);
      if (res?.status !== "success") {
        throw new Error(
          res?.status
            ? `NetSuite returned status: ${res.status}`
            : "NetSuite did not return a success status."
        );
      }
      return res;
    } catch (error) {
      console.error("Error creating NetSuite quote:", error);
      throw new Error(`Failed to create NetSuite quote. ${error.message}`);
    }
  }

  // ToDo: FNSI-101 - 2025-06-02 - Test & further this function logic.
  protected async prepOrderToCreateNetSuiteQuote(
    order: Order
  ): Promise<CreateNetSuiteQuotePayload> {
    if (!order.customer_netsuite_id) {
      throw new Error("NetSuite customer ID is missing.");
    }
    const shipping = order.shipping_details;
    const shippingAddress: CreateNetSuiteQuoteShippingAddress = {
      ...(shipping?.address_id ? { address_id: shipping.address_id } : {}),
      addressee: shipping?.addressee ?? "",
      attention: shipping?.attention ?? "",
      addr1: shipping?.address?.address_1 ?? "",
      city: shipping?.address?.city ?? "",
      state: shipping?.address?.state ?? "",
      zip: shipping?.address?.postal_code ?? "",
      country: shipping?.address?.country ?? "US",
      is_residential: /Address Type - Residential\./.test(
        shipping?.freight_instructions ?? ""
      )
    };
    const contactDetails = order.contact_details;
    const selectedContactId =
      order.customer_netsuite_contact_id ?? contactDetails?.contact_id;
    const quotePayload: CreateNetSuiteQuotePayload = {
      action: "createQuote",
      title: contactDetails?.quote_name ?? "",
      salesrep: this.user.netsuite_id,
      customerId: order.customer_netsuite_id,
      shippingInstructions: /Freight Estimate - Yes\./.test(shipping?.freight_instructions ?? "")
        ? shipping!.freight_instructions!
        : "",
      requestShippingQuote: /Freight Estimate - Yes\./.test(shipping?.freight_instructions ?? ""),
      sendEmail: order.contact_details?.send_email ?? false,
      ...(order.notes ? { quoteNotes: order.notes } : {}),
      ...(selectedContactId ? { contactId: selectedContactId } : {}),
      contact: {
        title: contactDetails?.title ?? null,
        firstname: contactDetails?.first_name ?? null,
        lastname: contactDetails?.last_name ?? null,
        phone: contactDetails?.phone ?? null,
        email: contactDetails?.email ?? ""
      },
      shippingAddress,
      items: [] as CreateNetSuiteQuoteLineItem[]
    };
    if (!order.items || order.items.length === 0) {
      throw new Error("No items provided to create a quote.");
    }
    try {
      for (const item of order.items) {
        // Check to see if the SKU exists within NetSuite first. If not, assign default values.
        const itemSku = item.sku || item.model || "";
        const netSuiteItem: FoundNetSuiteItemBySku = await this.netSuiteService.getNetSuiteItemBySku(
          itemSku
        );
        const nsItemId = netSuiteItem?.id;
        const netSuiteQuoteLineItem: CreateNetSuiteQuoteLineItem = {
          customitem: nsItemId ? 0 : 1, // 0 = known item, 1 = custom item requiring VUE_APP_CUSTOM_NETSUITE_ITEM_ID
          itemId: nsItemId
            ? nsItemId
            : Number(process.env.VUE_APP_CUSTOM_NETSUITE_ITEM_ID),
          quantity: item.qty,
          description: netSuiteItem?.description
            ? netSuiteItem.description
            : `Custom ${item.model} - ${item.display_name}`,
          msrp: item.unit_price || 0.01,
          pricelevel: order.customer_netsuite_price_level_id ?? 1, // default price level ID of 1 is "Base Price"
          customsku: itemSku,
          productionnotes: this.stringifyItemNotes(item)
        };
        quotePayload.items.push(netSuiteQuoteLineItem);
      }
      // Add surcharge percent to payload if env var is configured
      const surchargeVal = parseFloat(
        process.env.VUE_APP_PRICE_SURCHARGE_VALUE_NUMBER || ""
      );
      if (!isNaN(surchargeVal) && surchargeVal > 0) {
        quotePayload.surchargePercent = surchargeVal;
      }
    } catch (error) {
      console.error("Error preparing order for NetSuite quote:", error);
      throw new Error(
        `Failed to prepare order for NetSuite quote. ${error.message}`
      );
    }
    return quotePayload;
  }

  /* Note: FNSI-101 - Not in use yet */
  protected async getNetSuiteQuotes(): Promise<void> {
    const userNetSuiteQuotes: NetSuiteQuote[] = await this.netSuiteService.getMyQuotes();
    // Note: FNSI-101 - test block
    if (userNetSuiteQuotes.length > 0) {
      const fullQuote0 = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
        userNetSuiteQuotes[0]["quote id"]
      );
      // console.log("fullQuote0", fullQuote0);
      const fullQuote1 = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
        userNetSuiteQuotes[1]["quote id"]
      );
      // console.log("fullQuote", fullQuote1);
    }
    // Note: FNSI-101 - end test block
    /*
    // ToDo: FNSI-101 - move below block logic to appropriate location for whenever we're ready
    // to obtain the line items of the quotes. Confirming that the logic Works; however,
    // currently not needed right now in this location.

    if (userNetSuiteQuotes.length > 0) {
      const userFullNetSuiteQuotes: unknown[] = [];
      for (const quote of userNetSuiteQuotes) {
        const fullQuote = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
          quote["quote id"]
        );
        if (fullQuote) {
          userFullNetSuiteQuotes.push(fullQuote);
        }
      }
      console.log("userFullNetSuiteQuotes", userFullNetSuiteQuotes);
    }
    */
  }
}
